import { Link } from 'react-router-dom';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-surface-900 text-surface-200">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="text-brand-400 text-sm hover:underline mb-8 inline-block">&larr; Back to AutoCarPost</Link>
        <h1 className="text-3xl font-bold text-white mb-2">Privacy Policy</h1>
        <p className="text-surface-500 text-sm mb-8">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

        <div className="space-y-8 text-surface-300 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-white mb-3">1. Information We Collect</h2>
            <p className="mb-2"><strong>Account Information:</strong> When you register, we collect your name, email address, phone number, and business name (for dealer accounts).</p>
            <p className="mb-2"><strong>Vehicle Information:</strong> VIN, year, make, model, mileage, condition, pricing, and photos you upload for your listings.</p>
            <p className="mb-2"><strong>Usage Data:</strong> We automatically collect IP addresses, browser type, device information, and pages visited to improve the Service.</p>
            <p><strong>Payment Data:</strong> Payment processing is handled by Stripe. We store your Stripe customer ID but do not store credit card numbers or bank account details.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">2. How We Use Your Information</h2>
            <ul className="list-disc ml-6 space-y-1">
              <li>To create and manage your account</li>
              <li>To display your vehicle listings on AutoCarPost and connected marketplaces</li>
              <li>To process AI-powered vehicle analysis, descriptions, and market research</li>
              <li>To process payments and manage subscriptions</li>
              <li>To send service-related notifications (account updates, listing activity)</li>
              <li>To improve the Service and develop new features</li>
              <li>To prevent fraud and enforce our terms</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">3. Information Sharing</h2>
            <p>We share your information with third parties only as follows:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li><strong>Stripe:</strong> For payment processing and subscription management</li>
              <li><strong>Anthropic (Claude AI):</strong> Vehicle photos and data are sent to Claude for AI-powered analysis. Anthropic's data is processed per their <a href="https://www.anthropic.com/privacy" className="text-brand-400 hover:underline" target="_blank" rel="noopener noreferrer">privacy policy</a></li>
              <li><strong>NHTSA:</strong> VINs are sent to the National Highway Traffic Safety Administration for vehicle identification</li>
              <li><strong>Marketplace Platforms:</strong> When you opt to post listings, your vehicle information and photos are shared with selected platforms (Facebook Marketplace, etc.)</li>
              <li><strong>Public Listings:</strong> Vehicle information you choose to list publicly is visible to all visitors</li>
            </ul>
            <p className="mt-2">We do not sell your personal information to third parties.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">4. Data Storage and Security</h2>
            <p>Your data is stored on Cloudflare's global infrastructure (D1 database, R2 object storage, KV). We implement industry-standard security measures including:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Encrypted passwords (hashed, never stored in plaintext)</li>
              <li>JWT-based authentication with token expiration</li>
              <li>HTTPS encryption for all data in transit</li>
              <li>Rate limiting and brute-force protection</li>
              <li>Content Security Policy headers</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">5. Cookies and Local Storage</h2>
            <p>We use browser localStorage to store your authentication token for session persistence. We do not use third-party tracking cookies. Essential cookies may be used for security and functionality purposes.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">6. Data Retention</h2>
            <ul className="list-disc ml-6 space-y-1">
              <li><strong>Active accounts:</strong> Data is retained for as long as your account is active</li>
              <li><strong>Deleted accounts:</strong> Account data is deleted within 30 days of account deletion</li>
              <li><strong>Vehicle photos:</strong> Removed from storage when a vehicle is deleted</li>
              <li><strong>Activity logs:</strong> Retained for 90 days for security and analytics</li>
              <li><strong>Payment records:</strong> Retained as required by law (typically 7 years)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">7. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li><strong>Access:</strong> Request a copy of all data we hold about you</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
              <li><strong>Export:</strong> Download your vehicle data and listings</li>
              <li><strong>Opt-out:</strong> Unsubscribe from non-essential communications</li>
            </ul>
            <p className="mt-2">To exercise these rights, contact us at <a href="mailto:privacy@autocarpost.com" className="text-brand-400 hover:underline">privacy@autocarpost.com</a>.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">8. Children's Privacy</h2>
            <p>AutoCarPost is not intended for users under 18 years of age. We do not knowingly collect information from children. If you believe a child has provided us with personal information, please contact us.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">9. Changes to This Policy</h2>
            <p>We may update this Privacy Policy periodically. We will notify registered users of material changes via email. The "Last updated" date at the top indicates when this policy was last revised.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">10. Contact</h2>
            <p>For privacy-related questions or requests, contact us at <a href="mailto:privacy@autocarpost.com" className="text-brand-400 hover:underline">privacy@autocarpost.com</a>.</p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-surface-800 flex gap-6 text-xs text-surface-500">
          <Link to="/terms" className="hover:text-brand-400">Terms of Service</Link>
          <Link to="/acceptable-use" className="hover:text-brand-400">Acceptable Use Policy</Link>
        </div>
      </div>
    </div>
  );
}
